import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classes from './physicians.module.scss';
import Button from '../../_common/Form/Buttons/Button';
import InputField from '../../_common/Form/TextFields/InputField';
import Select from '../../_common/Form/Selects/Select/Select';
import { specOptions } from '../../../utils/data/data';
import { useQuery } from '../../../utils/hooks/useQuery';
import accountHelpers from '../../../utils/accountHelpers';
import { changeMenuPath } from '../../../store/actions/menu';
import utils from '../../../utils/utils';
import { getPhysiciansRequest } from '../../../store/actions/physicians';
import PhysiciansTable from './PhysiciansTable/PhysiciansTable';
import Loader from '../../_common/Loader/Loader';
import MergeModalContent from './MergeModalContent/MergeModalContent';
import Modal from '../../_common/Modal/Modal';
import { getHealthRequest } from '../../../store/actions/healths';

const headerPhysician = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Group',
    path: 'group',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Specialization',
    path: 'specialization',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Address',
    path: 'address',
  },
];

const headerHealth = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Hub Id',
    path: 'hubId',
  },
  {
    name: 'Rep',
    path: 'rep',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Address',
    path: 'address',
  },
];

let timeout;

const Physicians = ({ health }) => {
  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();

  const dispatch = useDispatch();
  const { physiciansList, physiciansTotalPage } = useSelector((store) => store.physicians);
  const { healthList, healthTotalPage } = useSelector((store) => store.healths);

  const initialData = useCallback((setWithQuery) => {
    let data = {
      userId: accountHelpers.getUser().id,
      page: 1,
      limit: 15,
      name: '',
      address: '',
      phone: '',
    };

    if (health) {
      data.hubId = '';
    } else {
      data.searchGroup = '';
      data.searchSpec = '';
    }

    if (setWithQuery) {
      data = _.forIn(data, (value, key) => {
        if (key === 'limit') {
          data[key] = query[key] || 15;
        } else if (key === 'page') {
          data[key] = query[key] || 1;
        } else {
          data[key] = query[key] || '';
        }
      });
    }

    return data;
  }, []);

  const [requestData, setRequestData] = useState(initialData(true));

  const {
    page, limit, searchGroup, address, phone, searchSpec, name, hubId,
  } = requestData;

  const [load, setLoad] = useState(true);
  const [isMergeModalOpen, mergeModalToggle] = useState(false);

  const physicianHeader = useMemo(() => (accountHelpers.getUser().role === 'Health'
    ? [
      {
        name: 'Name',
        path: 'name',
      },
      {
        name: 'Phone',
        path: 'phone',
      },
      {
        name: 'Address',
        path: 'address',
      },
    ]
    : headerPhysician), [accountHelpers.getUser().role]);

  useEffect(() => {
    (async () => {
      const data = utils.deleteEmptyKeys({ ...requestData });

      if (!health) {
        await dispatch(getPhysiciansRequest(data));
      } else {
        await dispatch(getHealthRequest(data));
      }

      setLoad(false);
    })();
  }, [pathname]);

  useEffect(() => {
    if (health) {
      dispatch(changeMenuPath('Health', `${pathname}${queryToStringify(query)}`));
    } else {
      dispatch(changeMenuPath('Physicians', `${pathname}${queryToStringify(query)}`));
    }
  }, [query]);

  const changeRequestData = async (path, value) => {
    const data = utils.deleteEmptyKeys({ ...requestData, [path]: value, page: 1 });

    setRequestData((prev) => ({ ...prev, [path]: value }));

    if (path !== 'searchSpec' && value.trim()) {
      setLoad(true);

      clearTimeout(timeout);

      timeout = setTimeout(async () => {
        if (health) {
          await dispatch(getHealthRequest(data));
        } else {
          await dispatch(getPhysiciansRequest(data));
        }

        delete data.userId;
        setQuery(data);
      }, 400);

      setLoad(false);
    } else {
      setLoad(true);

      if (health) {
        await dispatch(getHealthRequest(data));
      } else {
        await dispatch(getPhysiciansRequest(data));
      }

      setLoad(false);

      delete data.userId;
      setQuery(data);
    }
  };

  const onChangePageData = async (path, count) => {
    setLoad(true);

    const data = utils.deleteEmptyKeys({ ...requestData, [path]: count });

    if (path === 'limit') {
      data.page = 1;
    }
    if (health) {
      await dispatch(getHealthRequest(data));
    } else {
      await dispatch(getPhysiciansRequest(data));
    }

    setRequestData(data);

    setLoad(false);

    delete data.userId;

    setQuery(data);
  };

  const clearButtonClick = async () => {
    setLoad(true);

    setRequestData(initialData());

    const data = utils.deleteEmptyKeys(initialData());
    if (health) {
      await dispatch(getHealthRequest(data));
    } else {
      await dispatch(getPhysiciansRequest(data));
    }

    setLoad(false);

    delete data.userId;

    setQuery(data);
  };

  const header = health ? headerHealth : physicianHeader;

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h2>{health ? 'Select an Health Plan' : 'Select a Physician'}</h2>

        <div className={`${health ? classes.health_header_button : classes.header_button}`}>
          {!health && accountHelpers.getUser().role !== 'Health' && (
            <Button
              className={classes.merge_btn}
              title="Merge physician"
              type="button"
              onClick={() => mergeModalToggle(true)}
            />
          )}

          {((health && accountHelpers.getUser().role !== 'Rep')
            || (!health && accountHelpers.getUser().role !== 'Health')) && (
            <Button
              className={classes.add_btn}
              title={health ? 'Add a new Health Plan' : 'Add a new Physician'}
              type="button"
              href="add"
              reverseColor
              addButton
            />
          )}
        </div>
      </div>

      <div className={classes.filters}>
        <form
          className={classes.fields_wrapper}
          onSubmit={() => {
          }}
        >
          <div className={classes.fields_group}>
            <div className={classes.field_wrapper}>
              <InputField
                label="Name"
                value={name}
                onChange={(value) => changeRequestData('name', value)}
              />
            </div>

            <div className={classes.field_wrapper}>
              <InputField
                label={health ? 'Hub Id' : 'Group'}
                value={health ? hubId : searchGroup}
                onChange={(value) => changeRequestData(health ? 'hubId' : 'searchGroup', value)}
              />
            </div>

          </div>

          <div className={classes.fields_group}>
            <div className={classes.field_wrapper}>
              <InputField
                label="Address"
                value={address}
                onChange={(value) => changeRequestData('address', value)}
              />
            </div>

            <div className={health ? classes.field_wrapper_health : classes.field_wrapper}>
              <InputField
                label="Phone"
                value={phone}
                onChange={(value) => changeRequestData('phone', value)}
              />
            </div>

            {!health && (
              <div className={classes.field_wrapper}>
                <Select
                  label="Specialization"
                  options={specOptions}
                  currentOption={searchSpec}
                  onChange={(option) => changeRequestData('searchSpec', option)}
                />
              </div>
            )}

            <div className={classes.btns_wrapper}>

              <Button
                title="Clear"
                type="button"
                clearButton
                onClick={clearButtonClick}
              />
            </div>
          </div>

        </form>

        {/* <div className={classes.assign_btn_wrapper}> */}
        {/*  <Button */}
        {/*    title="Assign to Me" */}
        {/*    className={classes.assign_btn} */}
        {/*    type="button" */}
        {/*    reverseColor */}
        {/*  /> */}
        {/* </div> */}
      </div>
      {load ? <Loader className={classes.loader} />
        : (
          <PhysiciansTable
            data={health ? healthList : physiciansList}
            totalPage={health ? +healthTotalPage : +physiciansTotalPage}
            page={+page - 1}
            onChange={onChangePageData}
            limit={+limit}
            header={header}
            health={health}
          />
        )}

      <Modal
        open={isMergeModalOpen}
        onClose={() => mergeModalToggle(false)}
        className={classes.merge_modal}
      >
        <MergeModalContent
          onCancel={() => mergeModalToggle(false)}
        />
      </Modal>
    </div>
  );
};

Physicians.propTypes = {
  health: PropTypes.bool,
};

Physicians.defaultProps = {
  health: false,
};

export default Physicians;
